/**
 * The entity hub's vocabulary: every graph vertex a reader can open as a page,
 * what it is called, which graph view shows it best, and the one-line
 * definition the header prints under an unfamiliar kind.
 *
 * A benchmark and a dataset are stored as Method vertices in the graph; the API
 * says which (`entity.kind`), and the page shows the kind the reader expects.
 */
import { accentSurface } from '../../theme/mvkgTokens';

export const KINDS = {
  method:    { label: 'Method',    plural: 'methods',    tone: accentSurface.brand,   view: 'fusion' },
  // Blue, not amber: a benchmark is a kind, and amber reads as a warning.
  benchmark: { label: 'Benchmark', plural: 'benchmarks', tone: accentSurface.blue,    view: 'semantic' },
  dataset:   { label: 'Dataset',   plural: 'datasets',   tone: accentSurface.blue,    view: 'semantic' },
  problem:   { label: 'Problem',   plural: 'problems',   tone: accentSurface.rose,    view: 'problems' },
  task:      { label: 'Task',      plural: 'tasks',      tone: accentSurface.rose,    view: 'semantic' },
  technique: { label: 'Technique', plural: 'techniques', tone: accentSurface.cyan,    view: 'semantic' },
  component: { label: 'Component', plural: 'components', tone: accentSurface.cyan,    view: 'semantic' },
  claim:     { label: 'Claim',     plural: 'claims',     tone: accentSurface.purple,  view: 'claims' },
  author:    { label: 'Author',    plural: 'authors',    tone: accentSurface.sky,     view: 'authors' },
  direction: { label: 'Direction', plural: 'directions', tone: accentSurface.emerald, view: 'fusion' },
  concept:   { label: 'Concept',   plural: 'concepts',   tone: accentSurface.gray,    view: 'fusion' },
  survey:    { label: 'Survey',    plural: 'surveys',    tone: accentSurface.blue,    view: 'fusion' },
};

/** Route segments that older pages used, mapped to a hub kind. */
export const LEGACY_KIND = {
  topic: 'auto',
  environment: 'component',
};

/** The kind the API expects: a benchmark is a dataset-kind vertex there. */
export const apiKind = (k) => (k === 'benchmark' ? 'dataset' : k);

export const normalizeKind = (k) => {
  const s = String(k || '').toLowerCase();
  if (s === 'auto') return 'auto';
  return KINDS[s] ? s : (LEGACY_KIND[s] || null);
};

export const kindMeta = (k) => KINDS[normalizeKind(k)] || { label: 'Entity', plural: 'entities', tone: accentSurface.gray, view: 'fusion' };

/**
 * The hub page of a vertex. `collectionId` scopes ids that are only unique
 * inside one collection (directions).
 */
export function entityHref(kind, id, { collectionId, label } = {}) {
  const k = normalizeKind(kind);
  if (!k || id == null || id === '') return null;
  const q = new URLSearchParams();
  if (collectionId != null && collectionId !== '' && collectionId !== 'all') q.set('collection_id', collectionId);
  // The node's label helps the server resolve slug ids; it is a hint, not shown.
  if (label && /^[a-z]{1,4}_/i.test(String(id)) && !/^[a-z]_\d+$/i.test(String(id))) q.set('label', String(label).slice(0, 120));
  const qs = q.toString();
  return `/entity/${k}/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
}

/**
 * Best-effort kind for a graph node, from its id prefix and type. Returns null
 * when the node is not something the hub can open (a paper, a cluster).
 */
export function kindOfNode(node) {
  if (!node || node.id == null) return null;
  const id = String(node.id);
  const ek = String(node.entity_kind || '').toLowerCase();
  if (ek === 'benchmark' || ek === 'dataset' || ek === 'method') return ek;
  const type = String(node.type || node.group || '').toLowerCase();
  if (KINDS[type]) return type;
  if (type === 'topic') return 'auto';
  if (type === 'environment') return 'component';
  if (['breakthrough', 'consolidating', 'innovative'].includes(type)) return 'method';
  if (type === 'reference') return 'concept';
  if (type === 'paper' || type === 'article') return null;
  // Untyped: the loader's id prefixes. `c_` is shared: c_<number> is a claim,
  // c_<name> a component.
  if (/^c_\d+$/.test(id)) return 'claim';
  if (id.startsWith('m_')) return 'method';
  if (id.startsWith('a_')) return 'author';
  if (id.startsWith('dir_')) return 'direction';
  if (id.startsWith('p_')) return 'problem';
  if (id.startsWith('e_') || id.startsWith('task_')) return 'task';
  if (id.startsWith('benchmark_')) return 'benchmark';
  if (id.startsWith('d_') || id.startsWith('dataset_')) return 'dataset';
  if (id.startsWith('t_') || id.startsWith('technique_')) return 'technique';
  if (id.startsWith('c_') || id.startsWith('comp_')) return 'component';
  return null;
}
